/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {batch, computed, signal} from '@preact/signals-react';
import {createMutator} from '@ion/applications/src/store/SignalUtils';
import {getLocalStorage, setLocalStorage} from '@ion/core/src/capabilities/storage';
import {logReloadBehaviorChange, logThemeChange, logThemeModeChange} from './DLSThemeLogger';
import {DLSThemeModes, DLSThemes} from './types';

// dev-phase only: persists the developer's selected theme across reloads
// instead of always starting from legacy - remove once the current theme
// is the sole/default theme and this toggle is no longer needed ...
const THEME_STORAGE_KEY = 'ion-dls-selected-theme';

const getStoredTheme = (): DLSThemes | undefined => {
  const stored = getLocalStorage(THEME_STORAGE_KEY);
  return Object.values(DLSThemes).includes(stored) ? stored : undefined;
};

/**
 * The localStorage key which persists the user's light/dark mode choice
 * across reloads, so the choice is restored on the next visit.
 */
export const THEME_MODE_STORAGE_KEY = 'ion-dls-theme-mode';

const getStoredThemeMode = (): DLSThemeModes | undefined => {
  const stored = getLocalStorage(THEME_MODE_STORAGE_KEY);
  return Object.values(DLSThemeModes).includes(stored) ? stored : undefined;
};

/**
 * Tracks whether multi-theme switching is enabled. When `false`, the
 * application uses only the legacy theme. When `true`, theme switching
 * between legacy and current themes is enabled.
 */
export const multiThemeEnabled = signal<boolean>(true);

/**
 * Mutator function for enabling or disabling multi-theme support.
 */
export const setMultiThemeEnabled = createMutator(multiThemeEnabled);

export const addMultiThemeEnabledObserver = (callback: (enabled: boolean) => void) => {
  return multiThemeEnabled.subscribe(callback);
};

/**
 * Tracks the currently selected theme identifier.
 *
 * Values: Themes.legacy or Themes.current
 *
 * Initializes from the developer's last selection in localStorage when
 * present, falling back to Themes.legacy otherwise.
 */
export const currentTheme = signal<DLSThemes>(getStoredTheme() || DLSThemes.legacy);

/**
 * Mutator function for updating the currently selected theme. Persists the
 * selection to localStorage so it survives a reload instead of always
 * starting from Themes.legacy.
 */
export const setCurrentTheme = (newTheme: DLSThemes): void => {
  const oldTheme = currentTheme.value;
  currentTheme.value = newTheme;
  setLocalStorage(THEME_STORAGE_KEY, newTheme);
  logThemeChange(oldTheme, newTheme);
};

/**
 * Determines if a theme selection is saved in localStorage - one persisted by
 * `setCurrentTheme` - as opposed to the Themes.legacy default used when none
 * is saved. A saved value that is not a known theme does not count.
 *
 * ```typescript
 * import {hasStoredTheme, setCurrentTheme} from '@ion/dls/src/theme/ThemeStore';
 * import {DLSThemes} from '@ion/dls/src/theme/types';
 * ...
 *
 * // keep the user's saved theme, otherwise start in the current theme...
 * if (!hasStoredTheme()) {
 *   setCurrentTheme(DLSThemes.current);
 * }
 * ```
 *
 * @returns `true` when a known theme is saved, otherwise `false`.
 */
export const hasStoredTheme = (): boolean => getStoredTheme() !== undefined;

/**
 * Registers a callback to be invoked when the current theme changes.
 *
 * ```typescript
 * import {addThemeChangeObserver} from '@ion/dls/src/theme/ThemeStore';
 * import {DLSThemes} from '@ion/dls/src/theme/types';
 *
 * const unsubscribe = addThemeChangeObserver((theme: DLSThemes) => {
 *   console.log('Theme changed to:', theme);
 * });
 *
 * // cleanup...
 * unsubscribe();
 * ```
 *
 * @param   callback Function invoked when the theme changes, receives the new
 *          theme identifier.
 * @returns Unsubscribe function to remove the observer.
 */
export const addThemeChangeObserver = (callback: (theme: DLSThemes) => void) => {
  return currentTheme.subscribe(callback);
};

/**
 * Tracks the user's selected theme mode.
 *
 * Values: DLSThemeModes.light or DLSThemeModes.dark
 *
 * Initializes from the user's last selection in localStorage when present
 * and valid, falling back to DLSThemeModes.light otherwise. The selection
 * alone does not turn dark mode on; see `effectiveThemeMode`.
 */
export const themeMode = signal<DLSThemeModes>(getStoredThemeMode() || DLSThemeModes.light);

/**
 * Mutator function for updating the selected theme mode. Persists the
 * selection to localStorage by default so it is restored on the next visit;
 * pass `{persist: false}` to update the signal without writing storage.
 *
 * @param mode    The theme mode to select.
 * @param options Optional settings; `persist` defaults to `true`.
 */
export const setThemeMode = (mode: DLSThemeModes, {persist = true}: {persist?: boolean} = {}): void => {
  const oldMode = themeMode.value;
  themeMode.value = mode;
  if (persist) {
    setLocalStorage(THEME_MODE_STORAGE_KEY, mode);
  }
  logThemeModeChange(oldMode, mode);
};

/**
 * Registers a callback to be invoked when the selected theme mode changes.
 *
 * ```typescript
 * import {addThemeModeObserver} from '@ion/dls/src/theme/ThemeStore';
 * import {DLSThemeModes} from '@ion/dls/src/theme/types';
 * ...
 *
 * const unsubscribe = addThemeModeObserver((mode: DLSThemeModes) => {
 *   // react to the newly selected theme mode...
 * });
 *
 * // cleanup...
 * unsubscribe();
 * ```
 *
 * @param   callback Function invoked when the theme mode changes, receives the
 *          new theme mode.
 * @returns Unsubscribe function to remove the observer.
 */
export const addThemeModeObserver = (callback: (mode: DLSThemeModes) => void) => {
  return themeMode.subscribe(callback);
};

/**
 * Tracks whether the host application has opted in to dark mode. When
 * `false`, the application never renders dark regardless of the selected
 * theme mode. Not persisted; applications opt in at startup.
 */
export const darkModeEnabled = signal<boolean>(false);

/**
 * Mutator function for enabling or disabling dark mode support.
 */
export const setDarkModeEnabled = createMutator(darkModeEnabled);

/**
 * Registers a callback to be invoked when the dark mode opt-in changes.
 *
 * ```typescript
 * import {addDarkModeEnabledObserver} from '@ion/dls/src/theme/ThemeStore';
 * ...
 *
 * const unsubscribe = addDarkModeEnabledObserver((enabled: boolean) => {
 *   // react to the application opting in to, or out of, dark mode...
 * });
 *
 * // cleanup...
 * unsubscribe();
 * ```
 *
 * @param   callback Function invoked when the dark mode opt-in changes,
 *          receives the new opt-in value.
 * @returns Unsubscribe function to remove the observer.
 */
export const addDarkModeEnabledObserver = (callback: (enabled: boolean) => void) => {
  return darkModeEnabled.subscribe(callback);
};

/**
 * Derives whether the current theme is active; i.e. multi-theme switching is
 * enabled and the current theme is selected. This is the single definition
 * of the rule, shared by `effectiveThemeMode` and the theme mode toggle.
 */
export const isCurrentThemeActive = computed<boolean>(() => (
  multiThemeEnabled.value && currentTheme.value === DLSThemes.current
));

/**
 * Derives the theme mode which is actually applied. The result is dark only
 * when the application has opted in (`darkModeEnabled`), multi-theme
 * switching is enabled, the current theme is active, and the selected theme
 * mode is dark. Otherwise the result is light, so legacy and reseller
 * deployments never enter dark.
 */
export const effectiveThemeMode = computed<DLSThemeModes>(() => (
  darkModeEnabled.value &&
  isCurrentThemeActive.value &&
  themeMode.value === DLSThemeModes.dark ? DLSThemeModes.dark : DLSThemeModes.light
));

/**
 * Registers a callback to be invoked when the effective theme mode changes.
 *
 * ```typescript
 * import {addEffectiveThemeModeObserver} from '@ion/dls/src/theme/ThemeStore';
 * import {DLSThemeModes} from '@ion/dls/src/theme/types';
 * ...
 *
 * const unsubscribe = addEffectiveThemeModeObserver((mode: DLSThemeModes) => {
 *   // react to the newly applied theme mode...
 * });
 *
 * // cleanup...
 * unsubscribe();
 * ```
 *
 * @param   callback Function invoked when the effective theme mode changes,
 *          receives the new effective theme mode.
 * @returns Unsubscribe function to remove the observer.
 */
export const addEffectiveThemeModeObserver = (callback: (mode: DLSThemeModes) => void) => {
  return effectiveThemeMode.subscribe(callback);
};

/**
 * Returns the theme mode which is actually applied imperatively.
 *
 * ```typescript
 * import {getEffectiveMode} from '@ion/dls/src/theme/ThemeStore';
 * ...
 *
 * // DLSThemeModes.light or DLSThemeModes.dark...
 * const mode = getEffectiveMode();
 * ```
 *
 * @returns The effective theme mode.
 */
export const getEffectiveMode = (): DLSThemeModes => effectiveThemeMode.value;

/**
 * Determines if dark is the theme mode which is actually applied, read
 * imperatively; i.e. outside of a reactive subscription.
 *
 * ```typescript
 * import {isDarkMode} from '@ion/dls/src/theme/ThemeStore';
 * ...
 *
 * // pick the dark variant when dark mode is applied...
 * const color = isDarkMode() ? darkColor : lightColor;
 * ```
 *
 * @returns `true` when the effective theme mode is dark, otherwise `false`.
 */
export const isDarkMode = (): boolean => getEffectiveMode() === DLSThemeModes.dark;

/**
 * Determines if a value is a known `DLSThemeModes` value.
 *
 * ```typescript
 * import {isThemeMode} from '@ion/dls/src/theme/ThemeStore';
 * ...
 *
 * // only accept a known mode from an untrusted payload...
 * if (isThemeMode(payload.themeMode)) {
 *   ...
 * }
 * ```
 *
 * @param   mode The value to check.
 * @returns `true` when the value is a `DLSThemeModes` value, otherwise `false`.
 */
export const isThemeMode = (mode: unknown): mode is DLSThemeModes => (
  Object.values(DLSThemeModes).includes(mode as DLSThemeModes)
);

/**
 * Applies the effective theme mode a host Container has sent to a Remote
 * Micro-Frontend Component, the single place the mode rule is defined. A
 * payload without a mode is ignored, so a Remote whose host sends none is
 * unaffected. A valid `DLSThemeModes` value opts the Remote in to dark mode
 * and selects the mode without persisting it, since only the host owns the
 * stored selection. Any other value resolves to light and never enables dark
 * mode. The Remote must never bind the `<html data-theme-mode>` attribute;
 * only the host writes it.
 *
 * ```typescript
 * import {applyHostThemeMode} from '@ion/dls/src/theme/ThemeStore';
 * ...
 *
 * // apply the mode from a host message payload...
 * applyHostThemeMode(payload.themeMode);
 * ```
 *
 * @param mode The theme mode provided by the host, when present.
 */
export const applyHostThemeMode = (mode?: unknown): void => {
  if (mode !== undefined && mode !== null) {
    if (isThemeMode(mode)) {
      batch(() => {
        setDarkModeEnabled(true);
        setThemeMode(mode, {persist: false});
      });
    } else {
      setThemeMode(DLSThemeModes.light, {persist: false});
    }
  }
};

/**
 * Tracks whether a page reload should occur when theme changes.
 */
export const shouldReloadOnThemeChange = signal<boolean>(false);

/**
 * Mutator function for updating the reload behavior on theme change.
 */
export const setShouldReloadOnThemeChange = (enabled: boolean): void => {
  shouldReloadOnThemeChange.value = enabled;
  logReloadBehaviorChange(enabled);
};
